import React from "react";
import { motion } from "framer-motion";
import { Heart, PackageCheck, Sparkles, HandHeart } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import ContributionSlider from "@/components/ContributionSlider";

// Step 2 of the "found item" flow: warm thank-you + voluntary contribution.
export default function FoundThanksStep({ value, onChange }) {
  const { t } = useLang();
  return (
    <div className="space-y-6" data-testid="found-thanks-step">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-50 via-white to-rose-50 dark:from-teal-950/50 dark:via-slate-900 dark:to-rose-950/40 border border-teal-100 dark:border-teal-900/60 p-6 text-center">
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.15, type: "spring", stiffness: 220, damping: 14 }}
          className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-white shadow-sm text-findo-red">
          <HandHeart size={32} />
        </motion.div>
        <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-teal-600/10 text-teal-700 px-3 py-1 text-[11px] font-bold uppercase tracking-wide">
          <Sparkles size={12} /> {t("fthx.badge")}
        </span>
        <h2 className="mt-3 font-display text-2xl font-semibold text-findo-navy" data-testid="found-thanks-title">{t("fthx.title")}</h2>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-sm mx-auto">{t("fthx.text")}</p>
        <div className="mt-5 grid grid-cols-2 gap-3 text-left">
          <div className="rounded-2xl bg-white border border-border p-3 flex items-center gap-2.5">
            <PackageCheck size={18} className="text-teal-600 shrink-0" /><span className="min-w-0 hyphens-auto text-[11px] sm:text-xs font-semibold text-findo-navy">{t("fthx.impact1")}</span>
          </div>
          <div className="rounded-2xl bg-white border border-border p-3 flex items-center gap-2.5">
            <Heart size={18} className="text-findo-red shrink-0" /><span className="min-w-0 hyphens-auto text-[11px] sm:text-xs font-semibold text-findo-navy">{t("fthx.impact2")}</span>
          </div>
        </div>
      </motion.div>

      <div>
        <p className="font-display text-lg font-semibold text-findo-navy">{t("fthx.tipTitle")}</p>
        <p className="mt-1 text-sm text-slate-500 leading-relaxed">{t("fthx.tipText")}</p>
        <div className="mt-4"><ContributionSlider value={value} onChange={onChange} /></div>
        {value > 0 ? (
          <motion.p key="thx" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-3 text-sm font-semibold text-findo-red inline-flex items-center gap-1.5" data-testid="found-tip-thanks">
            <Heart size={14} className="fill-findo-red" /> {t("fthx.thanksTip")}
          </motion.p>
        ) : (
          <p className="mt-3 text-xs text-slate-400" data-testid="found-tip-optional">{t("fthx.optional")}</p>
        )}
      </div>
    </div>
  );
}
